import {
  AttendancePeriod,
  AttendanceShift,
  AttendanceShiftLocationTypeEnum,
  TimesheetOpenShiftFragment,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'
import { sortBy, times } from 'lodash'
import moment from 'moment'
import { DeepPartial } from 'utility-types'

import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import CompanyHoliday from 'modules/timeoff/store/models/CompanyHoliday'

import OpenShiftUtils from './OpenShiftUtils'
import { clockIn, isAutomaticBreak } from './ShiftConcern'

export enum HalfDayTypes {
  BEGINNING_OF_DAY = 'beginning_of_day',
  END_OF_DAY = 'end_of_day',
}

type AttendanceShiftWithBreakConfiguration = Pick<
  AttendanceShift,
  'minutes' | 'workable' | 'clockIn' | 'clockOut'
> & {
  timeSettingsBreakConfiguration?: DeepPartial<AttendanceShift['timeSettingsBreakConfiguration']>
}

const attendanceShiftLocationTypeMap: Record<string, AttendanceShiftLocationTypeEnum> = {
  business_trip: 'business_trip',
  office: 'office',
  work_from_home: 'work_from_home',
}

const NOT_DEFINED = 'not_defined'

class AttendanceShiftUtils {
  static newShift<T extends DeepPartial<AttendanceShift>>({
    date,
    employeeId,
    halfDay,
  }: {
    date: string
    employeeId: number
    halfDay: HalfDayTypes
  }): Pick<T, keyof AttendanceShift> {
    return {
      employeeId: employeeId,
      date: date,
      halfDay: halfDay,
      workable: true,
      crossesMidnight: false,
      isOvernight: false,
      minutes: 0,
      periodId: 0,
    }
  }

  static isNew(shift: Pick<AttendanceShift, 'id'>) {
    return !shift.id
  }

  static formattedDay(shiftDate: string) {
    return stringToDate(shiftDate, 'backend')
  }

  static clockInWithSeconds(
    shift: Pick<AttendanceShift, 'clockIn'> & DeepPartial<AttendanceShift>
  ) {
    return moment.utc(shift.clockIn).format('HH:mm:ss')
  }

  static getDay(shift: Pick<AttendanceShift, 'date'> & DeepPartial<AttendanceShift>) {
    return new Date(shift.date).getDate()
  }

  static clockIn(shift: Pick<AttendanceShift, 'clockIn' | 'date'>) {
    return clockIn(shift.clockIn, shift.date)
  }

  static clockInTimeStr(shift: Pick<AttendanceShift, 'clockIn' | 'date'>) {
    const clockInMoment = this.clockIn(shift)
    return dateToString(clockInMoment, 'ui_time')
  }

  static clockOutTimeStr(shift: Pick<AttendanceShift, 'clockOut' | 'date'>) {
    if (!shift.clockOut) return null

    const clockOutMoment = this.clockOut(shift)
    return dateToString(clockOutMoment, 'ui_time')
  }

  static clockOut(
    shift: Pick<AttendanceShift, 'clockOut' | 'date'> & DeepPartial<AttendanceShift>
  ) {
    const clockInTime = stringToDate(shift.clockOut, 'backend_datetime')

    return moment(`${shift.date} ${clockInTime.utc().format('HH:mm:ss')}`, 'YYYY/MM/D HH:mm:ss')
  }

  static isClosed(shiftClockOut?: string) {
    return !!shiftClockOut
  }

  static isFirstHalf(shift: Pick<AttendanceShift, 'halfDay'>) {
    return shift.halfDay === CompanyHoliday.HalfDayTypes.beginning_of_day
  }

  static isSecondHalf(shift: Pick<AttendanceShift, 'halfDay'>) {
    return shift.halfDay === CompanyHoliday.HalfDayTypes.end_of_day
  }

  static isHalfDay(shift: Pick<AttendanceShift, 'halfDay'>) {
    return AttendanceShiftUtils.isFirstHalf(shift) || AttendanceShiftUtils.isSecondHalf(shift)
  }

  static halfDayDuration(shift: Pick<AttendanceShift, 'halfDay'>): number {
    if (this.isHalfDay(shift)) {
      return 0.5
    }

    return 0
  }

  static hourlyDuration(
    shift: Pick<AttendanceShift, 'clockIn' | 'clockOut' | 'date' | 'minutes'>
  ): number {
    if (shift.clockIn && !shift.clockOut) {
      return Math.floor(moment.duration(moment().diff(this.clockIn(shift))).as('minutes'))
    }

    return shift.minutes || 0
  }

  static isAutomaticBreak(
    shift: Pick<AttendanceShift, 'workable' | 'automaticClockIn'> & DeepPartial<AttendanceShift>
  ) {
    return isAutomaticBreak(shift.workable ?? false, shift.automaticClockIn ?? false)
  }

  static isOpenShift(shift: Pick<AttendanceShift, 'clockIn' | 'clockOut'>) {
    return shift.clockIn && !shift.clockOut
  }

  static isEmpty(shift: Pick<AttendanceShift, 'clockIn' | 'clockOut'>) {
    return !shift.clockIn && !shift.clockOut
  }

  static locationType(
    shift?: Pick<AttendanceShift, 'locationType'>
  ): AttendanceShiftLocationTypeEnum | undefined {
    return shift?.locationType ? attendanceShiftLocationTypeMap[shift?.locationType] : undefined
  }

  static totalMinutesPerLocation(shifts: Pick<AttendanceShift, 'locationType' | 'minutes'>[]) {
    return shifts.reduce((counts: Record<string, number>, { locationType, minutes }) => {
      const key = locationType ?? NOT_DEFINED
      counts[key] = (counts[key] ?? 0) + minutes
      return counts
    }, {})
  }

  static totalMinutesPerLocationPercentage(
    shifts: Pick<AttendanceShift, 'locationType' | 'minutes'>[]
  ) {
    const totalMinutes = shifts.reduce((sum, shift) => sum + shift.minutes, 0)
    if (totalMinutes === 0) return []

    const counts = this.totalMinutesPerLocation(shifts)

    const locationLabels: Record<string, string> = {
      work_from_home: i18n.t('clock_in.sidepanel.clocked.work_location.home'),
      office: i18n.t('clock_in.sidepanel.clocked.work_location.office'),
      business_trip: i18n.t('clock_in.sidepanel.clocked.work_location.business_trip'),
      default: i18n.t('clock_in.sidepanel.clocked.work_location.not_defined'),
    }

    const entries = Object.entries(counts).map(([locationType, minutes]) => ({
      locationType,
      minutes,
      exact: (minutes / totalMinutes) * 100,
      rounded: Math.floor((minutes / totalMinutes) * 100),
    }))

    const totalRounded = entries.reduce((sum, entry) => sum + entry.rounded, 0)
    const remainder = 100 - totalRounded

    entries
      .sort((a, b) => b.exact - b.rounded - (a.exact - a.rounded))
      .slice(0, remainder)
      .forEach((entry) => (entry.rounded += 1))

    const translatedPercentages: Record<string, string> = {}
    entries
      .sort((a, b) => b.rounded - a.rounded)
      .forEach(({ locationType, rounded }) => {
        const label = locationLabels[locationType] || locationLabels.default
        if (label) {
          translatedPercentages[label] = `${rounded}%`
        }
      })

    return translatedPercentages
  }

  static currentShiftsForDate = <T extends Pick<AttendanceShift, 'referenceDate'>>(
    date: moment.Moment,
    shifts: T[]
  ) => {
    return shifts.filter((shift) => shift.referenceDate === dateToString(date, 'backend')) || []
  }

  static getPeriodForShift(
    shift: Pick<AttendanceShift, 'periodId'>,
    periods: AttendancePeriod[]
  ): AttendancePeriod | undefined {
    return periods.find((period) => period.id === shift.periodId)
  }

  static sortShiftsByDateAndClockIn = <T extends Pick<AttendanceShift, 'date' | 'clockIn'>>(
    shifts: T[]
  ) => {
    return sortBy(shifts, [(shift) => AttendanceShiftUtils.getDay(shift), (shift) => shift.clockIn])
  }

  static trackedPaidBreakMinutes = (shifts: AttendanceShiftWithBreakConfiguration[]) => {
    return shifts
      .filter(
        (shift) =>
          (!shift.workable &&
            !this.isOpenShift(shift) &&
            shift?.timeSettingsBreakConfiguration?.paid) ||
          false
      )
      .reduce((acc, shift) => acc + shift.minutes, 0)
  }

  static trackedMinutes = (
    shifts: Pick<AttendanceShift, 'minutes' | 'workable' | 'clockIn' | 'clockOut'>[]
  ) => {
    return shifts
      .filter((shift) => shift.workable && !this.isOpenShift(shift))
      .reduce((acc, shift) => acc + shift.minutes, 0)
  }

  static trackedBreaksdMinutes = (
    shifts: Pick<AttendanceShift, 'minutes' | 'workable' | 'clockIn' | 'clockOut'>[]
  ) => {
    return shifts
      .filter((shift) => !shift.workable && !this.isOpenShift(shift))
      .reduce((acc, shift) => acc + shift.minutes, 0)
  }

  static breakShits = (shifts: TimesheetPageShiftFragment[]) => {
    return shifts.filter((shift) => !shift.workable && !this.isOpenShift(shift))
  }

  static shiftsIds = (shifts: Pick<AttendanceShift, 'id'>[]) => {
    return shifts.map((shift) => shift.id)
  }

  static hasReviewsForDate = (date: moment.Moment, approvedDates?: string[] | undefined) => {
    const formattedDate = dateToString(date, 'backend')

    return !!approvedDates?.includes(formattedDate)
  }

  static isReadOnly = ({
    openShift,
    date,
    approvedDates,
    startsOn,
    canEditAttendance,
    currentPeriod,
  }: {
    openShift?: TimesheetOpenShiftFragment | null
    date: moment.Moment
    approvedDates?: string[] | undefined
    startsOn: moment.Moment | undefined
    canEditAttendance: boolean
    currentPeriod?: AttendancePeriod[] | undefined
  }) => {
    if (this.hasReviewsForDate(date, approvedDates) || !canEditAttendance || !currentPeriod?.length)
      return true

    const now = moment()
    const isFuture = date.isAfter(now)
    const isBeforeHiringDate = !!startsOn && date.isBefore(startsOn)
    const openShiftClockIn = !!openShift ? OpenShiftUtils.clockIn(openShift) : null
    const isAfterOpenShift = !!openShift && date.isAfter(openShiftClockIn)

    return isFuture || isBeforeHiringDate || isAfterOpenShift
  }

  static daysOfMonth = (month: moment.Moment) => {
    return times(month.daysInMonth()).map((offset) => month.clone().add(offset, 'day'))
  }

  static daysBetweenDates = (dateFrom: moment.Moment, dateTo: moment.Moment) => {
    const diff = dateTo.diff(dateFrom, 'days') + 1

    return times(diff).map((offset) => dateFrom.clone().add(offset, 'day'))
  }
}

export default AttendanceShiftUtils
